import axios from "axios";
import { Storage } from "@/services/Storage";

const DEFAULT_APP_NAME = "restaurant-pos";

function getAnnouncementApiBase() {
    if (typeof window !== "undefined" && window.announcementsApi) {
        return window.announcementsApi;
    }

    if (typeof window !== "undefined" && window.synergyApi) {
        return `${window.synergyApi}announcements`;
    }

    return "";
}

function pickFirst(source, keys, fallback = null) {
    if (!source || typeof source !== "object") return fallback;

    for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }

    return fallback;
}

function resolveBusinessType(source) {
    return pickFirst(source, [
        "businessType",
        "businessTypeId",
        "business_type_id",
        "business_type",
        "type",
        "storeType",
        "StoreType",
    ], null);
}

function resolveStoreDb(store) {
    return pickFirst(store, [
        "StoreDB",
        "storeDB",
        "database",
        "Database",
        "db",
        "DB",
    ], null);
}

async function fetchRegistrationDetails(storeDb) {
    const apiBase = typeof window !== "undefined" ? window.franchiseTomcatApi : "";
    const database = storeDb || await Storage.getCurrentStoreDB().catch(() => null);

    if (!apiBase || !database) return null;

    try {
        const { data } = await axios.get(`${apiBase}stores/getRegistrationDetails`, {
            params: { database },
        });
        return data || null;
    } catch (error) {
        console.warn("[announcements] failed to load registration details", error);
        return null;
    }
}

function normalizeArray(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (value === null || value === undefined || value === "") return [];

    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed.filter(Boolean);
        } catch (_) {
            return value
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
        }
    }

    return [value].filter(Boolean);
}

function normalizeBoolean(value, fallback = false) {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
        const lowered = value.trim().toLowerCase();
        if (["true", "1", "yes"].includes(lowered)) return true;
        if (["false", "0", "no"].includes(lowered)) return false;
    }

    return fallback;
}

function normalizeDisplayType(item) {
    const displayType = pickFirst(item, ["display_type", "displayType", "style"], "");
    const normalized = String(displayType || "").trim().toLowerCase();
    if (normalized) return normalized;

    const placement = String(pickFirst(item, ["placement"], "") || "").toLowerCase();
    if (placement.includes("modal")) return "modal";

    return "banner";
}

function normalizePlacement(item, displayType) {
    const placement = String(pickFirst(item, ["placement"], "") || "").trim().toLowerCase();
    if (placement) return placement;

    if (displayType === "modal") return "center";
    return "top";
}

function normalizeAnnouncement(item) {
    const displayType = normalizeDisplayType(item);

    return {
        id: pickFirst(item, ["id"]),
        title: pickFirst(item, ["title"], ""),
        message: pickFirst(item, ["message"], ""),
        imageUrl: pickFirst(item, ["image_url", "imageUrl"], null),
        link: pickFirst(item, ["link", "cta_url", "ctaUrl"], null),
        linkType: String(pickFirst(item, ["link_type", "linkType"], "external") || "external").toLowerCase(),
        ctaLabel: pickFirst(item, ["cta_label", "ctaLabel"], null),
        type: String(pickFirst(item, ["type"], "info") || "info").toLowerCase(),
        priority: Number(pickFirst(item, ["priority"], 999) || 999),
        displayType,
        placement: normalizePlacement(item, displayType),
        autoScroll: normalizeBoolean(pickFirst(item, ["auto_scroll", "autoScroll"], displayType === "ticker")),
        scrollSpeed: pickFirst(item, ["scroll_speed", "scrollSpeed"], "normal"),
        dismissible: normalizeBoolean(pickFirst(item, ["dismissible"], true), true),
        showOnce: normalizeBoolean(pickFirst(item, ["show_once", "showOnce"], false)),
        requiresAcknowledgement: normalizeBoolean(
            pickFirst(item, ["requires_acknowledgement", "requiresAcknowledgement"], false)
        ),
        isGlobal: normalizeBoolean(pickFirst(item, ["is_global", "isGlobal"], false)),
        targetApps: normalizeArray(pickFirst(item, ["target_apps", "targetApps"], [])),
        targetBusinessTypes: normalizeArray(
            pickFirst(item, ["target_business_types", "targetBusinessTypes"], [])
        ),
        targetCustomerIds: normalizeArray(
            pickFirst(item, ["target_customer_ids", "targetCustomerIds"], [])
        ),
        displayFrom: pickFirst(item, ["display_from", "displayFrom"], null),
        expiresAt: pickFirst(item, ["expires_at", "expiresAt"], null),
        createdAt: pickFirst(item, ["created_at", "createdAt"], null),
        createdBy: pickFirst(item, ["created_by", "createdBy"], null),
        raw: item,
    };
}

function sortAnnouncements(items) {
    return [...items].sort((left, right) => {
        const priorityDiff = Number(left.priority || 999) - Number(right.priority || 999);
        if (priorityDiff !== 0) return priorityDiff;

        const leftCreatedAt = new Date(left.createdAt || 0).getTime();
        const rightCreatedAt = new Date(right.createdAt || 0).getTime();
        return rightCreatedAt - leftCreatedAt;
    });
}

async function resolveAppContext(overrides = {}) {
    const [currentUser, storeDb, storeId] = await Promise.all([
        Storage.getCurrentUser().catch(() => null),
        Storage.getCurrentStoreDB().catch(() => null),
        Storage.getCurrentStoreID().catch(() => null),
    ]);

    const registrationDetails = await fetchRegistrationDetails(storeDb);

    return {
        app_name: overrides.app_name ?? DEFAULT_APP_NAME,
        business_type: overrides.business_type
            ?? overrides.businessType
            ?? overrides.businessTypeId
            ?? resolveBusinessType(registrationDetails),
        customer_id: overrides.customer_id ?? pickFirst(registrationDetails, [
            "mainCustomerID",
            "MainCustomerID",
            "customer_id",
            "customerId",
            "CustomerID",
            "mainID",
            "MainID",
        ], null),
        store_id: overrides.store_id ?? storeId ?? pickFirst(registrationDetails, [
            "id",
            "store_id",
            "storeId",
            "ID",
        ], null),
        user_id: overrides.user_id ?? pickFirst(currentUser, [
            "id",
            "user_id",
            "userId",
            "ID",
            "UserID",
            "EmployeeID",
        ], null),
    };
}

async function postInteraction(id, action, payload) {
    const baseUrl = getAnnouncementApiBase();
    if (!baseUrl || !id) return null;

    const { data } = await axios.post(`${baseUrl}/${id}/${action}`, payload);
    return data;
}

export const announcementsService = {
    async resolveContext(overrides = {}) {
        return resolveAppContext(overrides);
    },

    async fetchVisibleAnnouncements(overrides = {}) {
        const baseUrl = getAnnouncementApiBase();
        if (!baseUrl) return [];

        const context = await resolveAppContext(overrides);
        console.log("[announcements] visible payload", context);
        const { data } = await axios.post(`${baseUrl}/visible`, context);
        const normalized = Array.isArray(data) ? data.map(normalizeAnnouncement) : [];
        return sortAnnouncements(normalized);
    },

    async dismissAnnouncement(id, payload) {
        return postInteraction(id, "dismiss", payload);
    },

    async acknowledgeAnnouncement(id, payload) {
        return postInteraction(id, "acknowledge", payload);
    },

    async trackAnnouncementClick(id, payload) {
        return postInteraction(id, "click", payload);
    },
};
